import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8084';

const AuthContext = createContext(null);

const LS_USER  = 'ep_user';
const LS_TOKEN = 'ep_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(LS_USER);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.role) {
      localStorage.removeItem(LS_USER);
      localStorage.removeItem(LS_TOKEN);
      return null;
    }
    return parsed;
  });

  const [token, setToken] = useState(() =>
    localStorage.getItem(LS_USER) ? (localStorage.getItem(LS_TOKEN) || null) : null
  );

  if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

  // Unified login — tries /api/admin/auth/login (handles all roles),
  // falls back to /api/dealer/auth/login for dealers not on Users table.
  const login = useCallback(async (identifier, password) => {
    const isMobile = /^\d{10}$/.test(identifier);
    const payload  = isMobile ? { mobile_number: identifier, password } : { email: identifier, password };

    let res;
    try {
      res = await axios.post('/api/admin/auth/login', payload);
    } catch (err) {
      if (err.response?.status === 401) {
        res = await axios.post('/api/dealer/auth/login', payload);
      } else {
        throw err;
      }
    }

    const { token: newToken, user: userData } = res.data;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem(LS_TOKEN, newToken);
    localStorage.setItem(LS_USER, JSON.stringify(userData));
    // Store for force-password-change detection
    localStorage.setItem('ep_password', password);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    return userData;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.clear();
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // ── Backwards-compat aliases so existing pages need no changes ────────────
  const isDealer = user?.role === 'dealer';

  return (
    <AuthContext.Provider value={{
      // Primary
      user, token, login, logout,
      isAuthenticated: !!token,
      // Aliases for existing admin pages (use admin?.name, logout, etc.)
      admin: user,
      // Aliases for existing dealer pages (use dealer?.name, dealerLogout, etc.)
      dealer: user,
      dealerToken: isDealer ? token : null,
      dealerLogin: login,
      dealerLogout: logout,
      isDealerAuthenticated: !!(token && isDealer),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
